/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useLayoutEffect, useState } from "react";
import { useRefState } from "./use-ref-state";

// this is a port, cut down, and significant modification of react-resize-detector. I did keep a lot of core logic
// the same to make porting forward fixes easier, but the big change is it uses a `useRefState` vs `useRef`
// which means it doesn't screw up hooking up events when the ref target changes.

const createNotifier =
    (setSize: React.Dispatch<React.SetStateAction<ReactResizeDetectorDimensions>>) =>
    ({ width, height }: ReactResizeDetectorDimensions): void => {
        setSize(prev => {
            if (prev.width === width && prev.height === height) {
                return prev;
            }

            return { width, height };
        });
    };

interface ReactResizeDetectorDimensions {
    height?: number;
    width?: number;
}

export function useResizeDetector<T extends HTMLElement = HTMLElement>(): UseResizeDetectorReturn<T> {
    const [ref, el] = useRefState<T>();

    const [size, setSize] = useState<ReactResizeDetectorDimensions>({
        width: undefined,
        height: undefined,
    });

    useLayoutEffect(() => {
        const notifyResize = createNotifier(setSize);

        const resizeCallback: ResizeObserverCallback = entries => {
            for (const entry of entries) {
                const { width, height } = (entry && entry.contentRect) || {};
                notifyResize({ width, height });
            }
        };

        const resizeObserver = new window.ResizeObserver(resizeCallback);

        if (el) {
            resizeObserver.observe(el, undefined);
        }

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el]);

    return { ref, ...size };
}

interface UseResizeDetectorReturn<T> extends ReactResizeDetectorDimensions {
    ref: (el: T | null) => void;
}
